import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageIcon, Loader2, Upload, X } from 'lucide-react';

import {
  Organization,
  OrganizationBranding,
  OrganizationBrandingUpdate,
  BrandingTheme,
  OrgThemePreset,
} from '../../../types';
import organizationService from '../../../services/organizationService';
import { getApiBaseUrl } from '../../../services/apiClient';
import { useBrandingStore } from '../../../stores/brandingStore';
import { BRANDING_FONTS } from '../../../lib/branding';
import notify from '@/lib/notify';
import { presetSwatches } from '@/lib/themePresets';
import { apiErrorDetail } from '@/lib/knowledgeSharing';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/Common/Card';
import { Input } from '@/components/Common/input';
import { Label } from '@/components/Common/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Common/select';
import { ExperienceCard } from './ExperienceCard';
import { ThemeCard, ThemePresetPicker } from './ThemePresetPicker';
import { useOrgUiSettings } from './useOrgUiSettings';

interface CustomizationTabProps {
  organization: Organization;
  canEdit: boolean;
}

const NONE = '__none__'; // Select sentinel for "GenieFactory default"
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

const hasOverrides = (b: OrganizationBranding): boolean =>
  !!(b.primary_color || b.secondary_color || b.font_family);

/**
 * The colours a preset stands for, as shown on its card: swatches[1] is the
 * action colour and swatches[2] the second accent. The custom choice starts
 * from them.
 */
const themeColors = (preset: OrgThemePreset) => {
  const swatches = presetSwatches(preset);
  return { primary: swatches[1], secondary: swatches[2] };
};

/** Text input that saves on blur (or Enter) when its trimmed value changed. */
const AutoSaveInput: React.FC<{
  id: string;
  value: string;
  maxLength: number;
  placeholder: string;
  disabled: boolean;
  onSave: (value: string | null) => Promise<boolean>;
}> = ({ id, value, maxLength, placeholder, disabled, onSave }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = async () => {
    const next = draft.trim();
    if (next === value.trim()) return;
    if (!(await onSave(next || null))) setDraft(value);
  };

  return (
    <Input
      id={id}
      value={draft}
      maxLength={maxLength}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
    />
  );
};

/**
 * Custom colour, over the theme's. The native picker fires `input` while the
 * user drags (React's onChange) and `change` once it closes: only the latter
 * saves, so dragging does not flood the API.
 */
const ColorSetting: React.FC<{
  id: string;
  label: string;
  value: string;
  themeColor: string;
  disabled: boolean;
  onSave: (value: string | null) => Promise<boolean>;
}> = ({ id, label, value, themeColor, disabled, onSave }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const pickerRef = useRef<HTMLInputElement>(null);
  useEffect(() => setDraft(value), [value]);

  const commit = async (next: string) => {
    if (next.toLowerCase() === value.toLowerCase()) return;
    if (next && !HEX_COLOR.test(next)) {
      notify.error(t('branding.invalidColor', 'Couleur invalide : utilisez le format #RRGGBB.'));
      setDraft(value);
      return;
    }
    if (!(await onSave(next || null))) setDraft(value);
  };

  // Latest commit for the native listener, without re-binding it each render.
  const commitRef = useRef(commit);
  commitRef.current = commit;
  useEffect(() => {
    const picker = pickerRef.current;
    if (!picker) return;
    const onChange = () => void commitRef.current(picker.value);
    picker.addEventListener('change', onChange);
    return () => picker.removeEventListener('change', onChange);
  }, []);

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={pickerRef}
          id={id}
          type="color"
          value={draft || themeColor}
          onChange={(e) => setDraft(e.target.value)}
          disabled={disabled}
          className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent disabled:cursor-not-allowed"
          aria-label={label}
        />
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value.trim())}
          onBlur={() => void commit(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          placeholder={themeColor.toLowerCase()}
          disabled={disabled}
          className="w-32 font-mono"
          aria-label={label}
        />
      </div>
    </div>
  );
};

/**
 * Customization tab, in reading order: identity (logo, app name, tagline),
 * appearance (theme preset, colour and font overrides, default mode) and, for
 * admins, the experience card. Every control saves on its own: choices at
 * once, text and colours when the field is left.
 */
export const CustomizationTab: React.FC<CustomizationTabProps> = ({ organization, canEdit }) => {
  const { t } = useTranslation();
  const setBranding = useBrandingStore((s) => s.setBranding);
  const ui = useOrgUiSettings(organization.id);

  const [branding, setLocalBranding] = useState<OrganizationBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  // "Custom" is a choice of the theme grid: the theme preset stays the base
  // (surfaces, radius) and the branding overrides its colours and font.
  const [customMode, setCustomMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const applyBranding = (b: OrganizationBranding) => {
    setLocalBranding(b);
    setBranding(b);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const b = await organizationService.getBranding(organization.id);
        if (cancelled) return;
        applyBranding(b);
        setCustomMode(hasOverrides(b));
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.id]);

  const saveBranding = async (
    key: string,
    patch: OrganizationBrandingUpdate,
    { silent = false } = {}
  ): Promise<boolean> => {
    setSavingField(key);
    try {
      applyBranding(await organizationService.updateBranding(organization.id, patch));
      if (!silent) notify.success(t('orgExperience.saved'));
      return true;
    } catch (error) {
      notify.error(apiErrorDetail(error) ?? t('branding.saveError', 'Échec de l’enregistrement de la personnalisation.'));
      return false;
    } finally {
      setSavingField(null);
    }
  };

  const handleLogoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setLogoBusy(true);
        applyBranding(await organizationService.uploadLogo(organization.id, file));
      } catch {
        notify.error(t('branding.logoUploadError', 'Échec du téléversement du logo.'));
      } finally {
        setLogoBusy(false);
      }
    }
    // Reset so selecting the same file again re-triggers onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleLogoRemove = async () => {
    try {
      setLogoBusy(true);
      applyBranding(await organizationService.deleteLogo(organization.id));
    } catch {
      notify.error(t('branding.logoRemoveError', 'Échec de la suppression du logo.'));
    } finally {
      setLogoBusy(false);
    }
  };

  if (loading || ui.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (loadError || ui.loadError || !branding || !ui.settings) {
    return (
      <Card className="border-destructive/40">
        <CardHeader>
          <CardDescription className="text-destructive/90">
            {t('branding.loadError', 'Impossible de charger la personnalisation.')}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const settings = ui.settings;
  const brandingDisabled = !canEdit || savingField !== null;
  const uiDisabled = !canEdit || ui.saving !== null;
  const colors = themeColors(settings.theme_preset);

  // Custom starts from the current theme's colours, which the user then changes.
  const selectCustom = async () => {
    setCustomMode(true);
    if (hasOverrides(branding)) return;
    const saved = await saveBranding('custom', {
      primary_color: colors.primary.toLowerCase(),
      secondary_color: colors.secondary.toLowerCase(),
    });
    if (!saved) setCustomMode(false);
  };

  // A preset drops the custom colours and font, so the preset applies as designed.
  const selectPreset = async (theme_preset: OrgThemePreset) => {
    const presetChanges = theme_preset !== settings.theme_preset;
    if (hasOverrides(branding)) {
      const cleared = await saveBranding(
        'custom',
        { primary_color: null, secondary_color: null, font_family: null },
        { silent: presetChanges }
      );
      if (!cleared) return;
    }
    setCustomMode(false);
    if (presetChanges) await ui.save('theme', { theme_preset });
  };
  const spinner = (busy: boolean) =>
    busy ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null;

  return (
    <div className="space-y-4">
      {/* Identity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('orgCustomization.identity.title', 'Identité')}</CardTitle>
          <CardDescription>{t('orgExperience.identity.help')}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label>{t('branding.logo.title', 'Logo')}</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="hidden"
              onChange={handleLogoSelected}
              disabled={!canEdit || logoBusy}
            />
            {/* The tile is the upload control; the × in its corner removes the logo. */}
            <div className="group relative">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!canEdit || logoBusy}
                aria-label={
                  branding.logo_url
                    ? t('branding.logo.change', 'Changer le logo')
                    : t('branding.logo.upload', 'Téléverser un logo')
                }
                className={cn(
                  'relative flex h-[7.5rem] w-full items-center justify-center overflow-hidden rounded-md border bg-muted/40 transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default',
                  branding.logo_url ? 'border-border' : 'border-dashed border-border',
                  canEdit && 'hover:border-primary hover:bg-muted/60'
                )}
              >
                {branding.logo_url ? (
                  <img
                    src={`${getApiBaseUrl()}${branding.logo_url}`}
                    alt={organization.name}
                    className="max-h-20 max-w-[80%] object-contain"
                  />
                ) : (
                  <span className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                    {canEdit ? <Upload className="h-5 w-5" /> : <ImageIcon className="h-5 w-5" />}
                    {canEdit && t('branding.logo.upload', 'Téléverser un logo')}
                  </span>
                )}
                {canEdit && branding.logo_url && !logoBusy && (
                  <span className="absolute inset-0 flex items-center justify-center gap-2 bg-background/80 text-sm font-medium opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                    <Upload className="h-4 w-4" />
                    {t('branding.logo.change', 'Changer le logo')}
                  </span>
                )}
                {logoBusy && (
                  <span className="absolute inset-0 flex items-center justify-center bg-background/70">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  </span>
                )}
              </button>
              {canEdit && branding.logo_url && (
                <button
                  type="button"
                  onClick={handleLogoRemove}
                  disabled={logoBusy}
                  aria-label={t('branding.logo.remove', 'Supprimer')}
                  title={t('branding.logo.remove', 'Supprimer')}
                  className="absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {t(
                'branding.logo.description',
                "Remplace le logo GenieFactory en haut à gauche. PNG, JPG, SVG ou WebP — hauteur conseillée 40px, max 2 Mo."
              )}
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="org-ui-app_name" className="flex items-center gap-2">
                {t('orgExperience.identity.app_name')}
                {spinner(ui.saving === 'app_name')}
              </Label>
              <AutoSaveInput
                id="org-ui-app_name"
                value={settings.app_name ?? ''}
                maxLength={80}
                placeholder={t('orgExperience.identity.app_namePlaceholder')}
                disabled={uiDisabled}
                onSave={(app_name) => ui.save('app_name', { app_name })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="org-ui-tagline" className="flex items-center gap-2">
                {t('orgExperience.identity.tagline')}
                {spinner(ui.saving === 'tagline')}
              </Label>
              <AutoSaveInput
                id="org-ui-tagline"
                value={settings.tagline ?? ''}
                maxLength={200}
                placeholder={t('orgExperience.identity.taglinePlaceholder')}
                disabled={uiDisabled}
                onSave={(tagline) => ui.save('tagline', { tagline })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            {t('branding.appearance.title', 'Apparence')}
            {spinner(ui.saving === 'theme' || savingField !== null)}
          </CardTitle>
          <CardDescription>
            {t('branding.appearance.description', "Thème de l'interface, couleurs et police, mode clair ou sombre par défaut.")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ThemePresetPicker
            value={customMode ? null : settings.theme_preset}
            disabled={uiDisabled || brandingDisabled}
            onSelect={(preset) => void selectPreset(preset)}
          >
            <ThemeCard
              selected={customMode}
              disabled={uiDisabled || brandingDisabled}
              onSelect={() => void selectCustom()}
              swatches={[
                presetSwatches(settings.theme_preset)[0],
                branding.primary_color || colors.primary,
                branding.secondary_color || colors.secondary,
              ]}
              label={t('branding.custom.label', 'Personnalisé')}
              description={t(
                'branding.custom.description',
                'Part des couleurs du thème {{theme}} : changez la couleur principale, l’accent et la police.',
                { theme: t(`orgExperience.theme.presets.${settings.theme_preset}.label`) }
              )}
              footnote={branding.font_family}
            />
          </ThemePresetPicker>

          {customMode && (
            <div className="grid gap-6 rounded-md border border-border p-4 sm:grid-cols-3">
              <ColorSetting
                id="branding-color"
                label={t('branding.color.label', 'Couleur principale')}
                value={branding.primary_color ?? ''}
                themeColor={colors.primary}
                disabled={brandingDisabled}
                onSave={(primary_color) => saveBranding('primary_color', { primary_color })}
              />
              <ColorSetting
                id="branding-secondary-color"
                label={t('branding.secondaryColor.label', 'Accent secondaire')}
                value={branding.secondary_color ?? ''}
                themeColor={colors.secondary}
                disabled={brandingDisabled}
                onSave={(secondary_color) => saveBranding('secondary_color', { secondary_color })}
              />
              <div className="space-y-2">
                <Label>{t('branding.font.label', 'Police')}</Label>
                <Select
                  value={branding.font_family || NONE}
                  onValueChange={(v) => void saveBranding('font_family', { font_family: v === NONE ? null : v })}
                  disabled={brandingDisabled}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('branding.font.default', 'Police du thème')}</SelectItem>
                    {BRANDING_FONTS.map((font) => (
                      <SelectItem key={font} value={font}>
                        <span style={{ fontFamily: `'${font}', sans-serif` }}>{font}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>{t('branding.theme.label', 'Mode par défaut')}</Label>
            <p className="text-xs text-muted-foreground">
              {t('branding.theme.hint', "Appliqué aux membres qui n’ont pas encore choisi leur propre mode.")}
            </p>
            <Select
              value={(branding.default_theme as BrandingTheme | undefined) || NONE}
              onValueChange={(v) =>
                void saveBranding('default_theme', { default_theme: v === NONE ? null : (v as BrandingTheme) })
              }
              disabled={brandingDisabled}
            >
              <SelectTrigger className="w-60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t('branding.theme.system', 'Défaut système')}</SelectItem>
                <SelectItem value="light">{t('branding.theme.light', 'Clair')}</SelectItem>
                <SelectItem value="dark">{t('branding.theme.dark', 'Sombre')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Experience and modules are an admin decision: members don't see them. */}
      {canEdit && <ExperienceCard ui={ui} canEdit={canEdit} />}
    </div>
  );
};

export default CustomizationTab;
